<template>
  <d-modal
    :modal="modal"
    :title="
      editMode
        ? $t('projects.edit.page-heading')
        : $t('projects.add.page-heading')
    "
    @submitModal="handleSubmitModal"
    @closeModal="handleCloseModal"
    :isSubmitting="isSubmitting"
  >
    <template slot="body">
      <d-loading class="mb-4" v-if="loading.page"></d-loading>
      <v-row class="mb-1" v-else>
        <v-col cols="12" md="6" class="mb-3">
          <v-text-field
            :label="$t('projects.add.form.name')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.name"
            :rules="rule.required"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" class="mb-3">
          <v-select
            filled
            hide-details="auto"
            :items="projectOwners"
            item-text="displayText"
            item-value="value"
            clearable
            :label="$t('projects.add.form.project-owner')"
            v-model="form.projectOwnerCode"
            :rules="rule.required"
          ></v-select>
        </v-col>
        <v-col cols="12" md="12" class="mb-3">
          <v-textarea
            :label="$t('projects.add.form.description')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.description"
            :rules="rule.required"
            rows="1"
          ></v-textarea>
        </v-col>
        <v-col cols="12" md="12" class="mb-3">
          <v-text-field
            :label="$t('projects.add.form.slug')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.slug"
            :rules="rule.slug"
            :error="errorMessages.slug.length > 0"
            :error-messages="errorMessages.slug"
            @blur="checkSlugIsValid"
            :loading="isCheckingSlug"
            @keyup="lowerCaseSlug"
          ></v-text-field>
        </v-col>
        <v-col cols="12" class="mb-3">
          <v-textarea
            :label="$t('projects.add.form.base-url')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.baseUrl"
            :rules="rule.required"
            rows="1"
          ></v-textarea>
        </v-col>
        <v-col cols="12" class="mb-3">
          <v-autocomplete
            filled
            hide-details="auto"
            :items="authenticationTypes"
            item-text="displayText"
            item-value="value"
            clearable
            :label="$t('projects.add.form.authentication-type')"
            v-model="form.authenticationTypeCode"
            :rules="rule.required"
          ></v-autocomplete>
        </v-col>

        <template
          v-if="form.authenticationTypeCode == ProjectAuthenticationTypes.BASIC"
        >
          <v-col cols="12" class="mb-3">
            <v-text-field
              :label="$t('projects.add.form.basic.username')"
              hide-details="auto"
              filled
              type="text"
              v-model="form.basicAuth.username"
              :rules="rule.required"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="mb-3">
            <v-text-field
              :label="$t('projects.add.form.basic.password')"
              hide-details="auto"
              filled
              type="text"
              v-model="form.basicAuth.password"
              :rules="rule.required"
            ></v-text-field>
          </v-col>
        </template>

        <template
          v-else-if="
            form.authenticationTypeCode == ProjectAuthenticationTypes.BEARER
          "
        >
          <v-col cols="12" class="mb-3">
            <v-textarea
              :label="$t('projects.add.form.bearer.token')"
              hide-details="auto"
              filled
              type="text"
              v-model="form.bearerAuth.token"
              :rules="rule.required"
              rows="1"
            ></v-textarea>
          </v-col>
        </template>

        <template
          v-else-if="
            form.authenticationTypeCode == ProjectAuthenticationTypes.CUSTOM
          "
        >
          <v-col cols="12" class="mb-3">
            <v-text-field
              :label="$t('projects.add.form.custom.header-key')"
              hide-details="auto"
              filled
              type="text"
              v-model="form.customAuth.headerKey"
              :rules="rule.required"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="mb-3">
            <v-textarea
              :label="$t('projects.add.form.custom.token')"
              hide-details="auto"
              filled
              type="text"
              v-model="form.customAuth.token"
              :rules="rule.required"
              rows="1"
            ></v-textarea>
          </v-col>
        </template>
      </v-row>
    </template>
  </d-modal>
</template>
  
  <script>
import {
  checkSlugAvailability,
  addProject,
  getProjectById,
  updateProject,
} from "@/services/project";

import ProjectAuthenticationTypes from "@/enums/ProjectAuthenticationTypes.js";

const initialForm = {
  id: null,
  name: null,
  authenticationTypeCode: null,
  baseUrl: null,
  description: null,
  projectOwnerCode: null,
  slug: null,
  basicAuth: {
    password: null,
    username: null,
  },
  bearerAuth: {
    token: null,
  },
  customAuth: {
    headerKey: null,
    token: null,
  },
};

const initialErrorMessages = {
  slug: [],
};

export default {
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    item: Object,
  },
  data() {
    return {
      ProjectAuthenticationTypes,
      isSubmitting: false,
      form: { ...initialForm },
      rule: {
        slug: [
          (v) => !!v || this.$t("defaults.rules.required"),
          (v) =>
            /^[a-z0-9-]{1,25}$/.test(v?.toLowerCase()) ||
            this.$t("projects.add.rules.slug-invalid"),
        ],
      },
      projectOwners: [],
      authenticationTypes: [],
      errorMessages: { ...initialErrorMessages },
      isCheckingSlug: false,
      loading: {
        page: false,
      },
    };
  },
  computed: {
    editMode() {
      return !!this.item?.id;
    },
  },
  methods: {
    lowerCaseSlug() {
      this.form.slug = this.form?.slug?.toLowerCase();
    },
    async handleSubmitModal() {
      if (this.editMode) {
        await this.submitUpdateProject();
        return;
      }
      await this.submitAddProject();
    },
    async submitAddProject() {
      try {
        this.isSubmitting = true;
        await addProject(this.form);

        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("projects.add.alert.added"),
        });
        this.$emit("submitModal");
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitUpdateProject() {
      try {
        this.isSubmitting = true;
        await updateProject(this.form);

        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("projects.edit.alert.updated"),
        });
        this.$emit("submitModal");
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    handleCloseModal() {
      this.$emit("closeModal");
    },
    async checkSlugIsValid() {
      const { slug, id } = this.form;
      if (!slug) return;

      if (/^[a-z0-9-]{1,25}$/.test(slug?.toLowerCase())) {
        try {
          this.isCheckingSlug = true;
          const response = await checkSlugAvailability({
            slug,
            id: this.editMode ? id : null,
          });
          if (!response) {
            this.errorMessages.slug = [
              this.$t("projects.add.rules.slug-is-taken"),
            ];
            return;
          }
          this.errorMessages.slug = [];
        } finally {
          this.isCheckingSlug = false;
        }
      }
    },
    async getProjectById() {
      try {
        const { data, items } = await getProjectById(this.item?.id || -1);
        this.projectOwners = items.projectOwners;
        this.authenticationTypes = items.authenticationTypes;

        this.form = {
          ...data,
          id: data.id ? data.id : this.item?.id || -1,
          basicAuth: data.basicAuth ? data.basicAuth : initialForm.basicAuth,
          customAuth: data.customAuth
            ? data.customAuth
            : initialForm.customAuth,
          bearerAuth: data.bearerAuth
            ? data.bearerAuth
            : initialForm.bearerAuth,
        };
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      }
    },
  },
  async mounted() {
    try {
      this.loading.page = true;
      await this.getProjectById();
    } finally {
      this.loading.page = false;
    }
  },
};
</script>
  